import { AlertTriangle, Loader2 } from "lucide-react";

import { ScrollArea, ScrollBar } from "~/components/ui/scroll-area";
import type { TPartImage } from "~/database/types";
import { cn } from "~/lib/utils";

interface PartImageWithSignedUrl extends TPartImage {
    signedUrl: string | null;
}

interface PartImageGalleryProps {
    images: PartImageWithSignedUrl[];
    selectedImageId: string | null;
    onSelectImage: (imageId: string) => void;
    className?: string;
}

export function PartImageGallery({
    images,
    selectedImageId,
    onSelectImage,
    className,
}: PartImageGalleryProps) {
    if (!images || images.length === 0) {
        return (
            <div
                className={cn(
                    "text-center text-muted-foreground p-4",
                    className,
                )}
            >
                No images generated yet.
            </div>
        );
    }

    return (
        <ScrollArea
            className={cn(
                "w-full whitespace-nowrap rounded-md border",
                className,
            )}
        >
            <div className="flex w-max space-x-2 p-2">
                {images
                    .sort(
                        (a, b) =>
                            new Date(b.createdAt).getTime() -
                            new Date(a.createdAt).getTime(),
                    )
                    .map((image) => (
                        <figure
                            key={image.id}
                            className={cn(
                                "shrink-0 cursor-pointer relative border-2 rounded-md overflow-hidden transition-all",
                                selectedImageId === image.id
                                    ? "border-brand-terra-500 ring-2 ring-brand-terra-200 shadow-lg bg-brand-cream-100/50"
                                    : "border-gray-200 hover:border-gray-400",
                                "hover:shadow-sm",
                            )}
                            onClick={() => onSelectImage(image.id)}
                        >
                            <div className="overflow-hidden rounded-md">
                                {image.signedUrl ? (
                                    <img
                                        src={image.signedUrl}
                                        alt={`Part image generated at ${new Date(
                                            image.createdAt,
                                        ).toLocaleString()}`}
                                        className="aspect-square size-24 object-cover" // Fixed size for thumbnails
                                        width={96}
                                        height={96}
                                    />
                                ) : image.status === "COMPLETED" ? (
                                    <div className="flex aspect-square size-24 items-center justify-center bg-muted">
                                        <span className="text-xs text-muted-foreground">
                                            No URL
                                        </span>
                                    </div>
                                ) : image.status === "ERROR" ? (
                                    <div className="flex aspect-square size-24 items-center justify-center bg-muted">
                                        <span className="text-xs text-muted-foreground">
                                            <AlertTriangle className="size-4" />
                                        </span>
                                    </div>
                                ) : (
                                    <div className="flex aspect-square size-24 items-center justify-center bg-muted">
                                        <span className="text-xs text-muted-foreground">
                                            <Loader2 className="size-4 animate-spin" />
                                        </span>
                                    </div>
                                )}
                            </div>
                            {/* Optional: Add overlay for status like CREATED/IN_PROGRESS/ERROR if needed */}
                            {/* {(image.status === "CREATED" || image.status === "IN_PROGRESS") && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              </div>
            )} */}
                            {/* {(image.status === "ERROR") && (
              <div className="absolute inset-0 bg-red-900/50 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-white" />
              </div>
            )} */}
                        </figure>
                    ))}
            </div>
            <ScrollBar orientation="horizontal" />
        </ScrollArea>
    );
}
